import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, User, CreditCard, Calendar, Check, X, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccountSettings() {
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchPaymentHistory();
    }
  }, [currentUser]);

  const fetchPaymentHistory = async () => {
    try {
      // Note: This endpoint would need to be created in backend
      const response = await axios.get(`${API}/users/${currentUser.uid}/payments`);
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      // If endpoint doesn't exist yet, set empty array
      setPaymentHistory([]);
    }
  };

  const handleDowngradeToFree = async () => {
    if (!confirm('Are you sure you want to downgrade to the Free plan? You will lose access to Pro features immediately.')) {
      return;
    }

    setIsDowngrading(true);
    try {
      await axios.put(`${API}/users/profile/${currentUser.uid}`, {
        subscription_tier: 'free'
      });
      
      toast.success('Successfully downgraded to Free plan');
      // Reload to update user profile
      window.location.reload();
    } catch (error) {
      console.error('Error downgrading subscription:', error);
      toast.error('Failed to downgrade subscription. Please try again.');
    } finally {
      setIsDowngrading(false);
    }
  };

  const handleCancelSubscription = () => {
    toast.info('To cancel your subscription, please contact support at support@podmixrlabs.com', {
      duration: 7000,
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  const isPro = userProfile?.subscription_tier === 'pro';
  const recordingCount = userProfile?.recording_count_current_month || 0;
  const recordingLimit = isPro ? 'Unlimited' : 5;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-6">
            <div className="logo-placeholder"></div>
            <div className="border-l border-gray-200 pl-6">
              <h1 className="text-2xl font-bold font-manrope text-gray-900">Account Settings</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/studio')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Studio
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Account Information */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Account Information</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="text-lg font-medium text-gray-900">{currentUser.displayName || 'Not set'}</p>
              </div>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-lg font-medium text-gray-900">{currentUser.email}</p>
              </div>
            </div>
            
            <div className="flex justify-between items-center py-3">
              <div>
                <p className="text-sm text-gray-600">Account Created</p>
                <p className="text-lg font-medium text-gray-900">
                  {new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Subscription Status */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Subscription</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Current Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-2xl font-bold text-gray-900">{isPro ? 'Pro' : 'Free'}</p>
                  {isPro && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                      Active
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                {isPro ? (
                  <p className="text-2xl font-bold text-gray-900">£9.99/month</p>
                ) : (
                  <p className="text-lg font-medium text-gray-600">£0</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-600">Recordings This Month</p>
                <p className="text-lg font-medium text-gray-900">{recordingCount} / {recordingLimit}</p>
              </div>
            </div>

            {isPro ? (
              <div className="space-y-3 pt-4">
                <div className="flex items-start gap-2 text-green-600">
                  <Check className="w-5 h-5 mt-0.5" />
                  <span>Unlimited recordings</span>
                </div>
                <div className="flex items-start gap-2 text-green-600">
                  <Check className="w-5 h-5 mt-0.5" />
                  <span>AI audio & video enhancement</span>
                </div>
                <div className="flex items-start gap-2 text-green-600">
                  <Check className="w-5 h-5 mt-0.5" />
                  <span>AI transcript editing</span>
                </div>
                <div className="flex items-start gap-2 text-green-600">
                  <Check className="w-5 h-5 mt-0.5" />
                  <span>Priority support</span>
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    onClick={handleDowngradeToFree}
                    disabled={isDowngrading}
                    variant="outline"
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    {isDowngrading ? 'Processing...' : 'Downgrade to Free'}
                  </Button>
                  <Button
                    onClick={handleCancelSubscription}
                    variant="outline"
                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  >
                    Cancel Subscription
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    Need help? Contact <a href="mailto:support@podmixrlabs.com" className="text-blue-600 hover:underline">support@podmixrlabs.com</a>
                  </p>
                </div>
              </div>
            ) : (
              <div className="pt-4">
                <Button
                  onClick={() => navigate('/studio')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Upgrade to Pro - £9.99/month
                </Button>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Get unlimited recordings and AI-powered features
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Payment History */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
          </div>
          
          {paymentHistory.length > 0 ? (
            <div className="space-y-3">
              {paymentHistory.map((payment, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {payment.payment_status === 'paid' ? 'Payment successful' : 'Payment pending'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      £{payment.amount.toFixed(2)}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      payment.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {payment.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <X className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No payment history yet</p>
              {!isPro && (
                <p className="text-sm text-gray-500 mt-2">
                  Upgrade to Pro to start making payments
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
